import { useState } from 'react';
import { Switch, Tooltip } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Field, Input, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { WIDGET_MAU_MAC_DINH } from '../../../../styles/tokens';
import { doanMaNhung } from '../../constants-bot';
import { useXoayKhoaNhung } from '../../hooks/useBot';
import { useDanhSachNguon, useSucKhoeTroLy, useVaiTroToChuc } from '../../hooks/useChung';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { useMaTranKhuonNhieu, useQuyenHieuLuc, useTaiKhoanQuyen } from '../../hooks/useQuyen';
import {
  MAU_HOP_LE,
  danhGiaVaiNhung,
  laCoVanDe,
  mauSauChuSo,
  moMoiGoc,
  soNguonDocDuoc,
} from '../../nghiep-vu/cau-hinh-bot';
import { khoaModel, nhanModel, nhanTuKhoa } from '../../nghiep-vu/ten-model';
import type { AssistantHealth, KhuonGrant, MucModel, WidgetPosition } from '../../types';
import { HopXacNhan } from '../chung/HopXacNhan';
import { Khoi, ODanhSachDong, type LuaChon, type TabProps } from './chung';

/**
 * Tên một mô hình ở màn này: tên hiển thị trong "Thiết lập Model AI", để hai màn gọi một mô hình bằng cùng một tên.
 * Danh mục chưa có tên thì rơi về TÊN THẬT, không về tên chung — đây là màn của người vận hành, hai dòng cùng hiện "FoxAI"
 * thì không chọn được.
 */
function tenTrongDanhMuc(m: MucModel): string {
  return m.tenCongKhai || nhanModel(m);
}

/** Tên mục mặc định cho chữ mờ của ô chọn. Dò không ra thì trả mã thô, đừng đoán. */
function tenMucMacDinh(h: AssistantHealth | undefined): string {
  const m = h?.models?.find((x) => x.macDinh) ?? h?.models?.find((x) => x.model === h?.model);
  return m ? tenTrongDanhMuc(m) : (h?.model ?? '');
}

type CauMau = { chu: string; mau: string };

/**
 * Khoá nhúng và đoạn mã — hai thứ người dùng phải MANG ĐI, nên phải chép được.
 *
 * Nút con mắt dù khoá không phải bí mật: người vận hành mở màn này khi trình chiếu, chia sẻ màn hình, chụp ảnh gửi hỗ
 * trợ. Che mặc định biến việc để lộ thành hành động CÓ Ý. Chép được KỂ CẢ khi đang ẩn.
 */
function KhoiNhung({
  khoa, botId, onXoay, suaDuoc,
}: {
  khoa: string;
  /** `null` = bot chưa lưu lần nào, chưa có gì để xoay. */
  botId: string | null;
  onXoay: (moi: string) => void;
  suaDuoc: boolean;
}) {
  const { t } = useT();
  const toast = useToast();
  const [hien, setHien] = useState(false);
  const [hoiXoay, setHoiXoay] = useState(false);
  const xoay = useXoayKhoaNhung();
  const doanMa = doanMaNhung(khoa);

  const chep = async (text: string, nhan: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('troLyAiBotNhung.daSaoChep', { ten: nhan }));
    } catch {
      // Trang http nội bộ không có `navigator.clipboard`. Nói ra thay vì im.
      toast.error(t('troLyAiBotNhung.khongSaoChepDuoc'));
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 text-xs font-medium text-content-secondary">{t('troLyAiBotNhung.khoaNhung')}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Input
            readOnly
            aria-label={t('troLyAiBotNhung.khoaNhung')}
            className="min-w-0 flex-1 font-mono text-xs"
            value={hien ? khoa : '•'.repeat(Math.min(khoa.length, 32))}
          />
          <Tooltip title={hien ? t('troLyAiBotNhung.anKhoa') : t('troLyAiBotNhung.hienKhoa')}>
            <Button
              size="sm"
              variant="ghost"
              aria-label={hien ? t('troLyAiBotNhung.anKhoa') : t('troLyAiBotNhung.hienKhoa')}
              icon={hien ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setHien((v) => !v)}
            />
          </Tooltip>
          <Button size="sm" variant="secondary" onClick={() => void chep(khoa, t('troLyAiBotNhung.khoa'))}>
            {t('troLyAiBotNhung.saoChep')}
          </Button>
          {botId && suaDuoc ? (
            <Tooltip title={t('troLyAiBotNhung.xoayKhoa')}>
              <Button
                size="sm"
                variant="ghost"
                loading={xoay.isPending}
                aria-label={t('troLyAiBotNhung.xoayKhoa')}
                icon={<ReloadOutlined />}
                onClick={() => {
                  xoay.reset();
                  setHoiXoay(true);
                }}
              />
            </Tooltip>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-content-muted">{t('troLyAiBotNhung.khoaGhiChu')}</p>
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-content-secondary">{t('troLyAiBotNhung.doanMaDan')}</p>
          <Button size="sm" variant="ghost" onClick={() => void chep(doanMa, t('troLyAiBotNhung.doanMa'))}>
            {t('troLyAiBotNhung.saoChepDoanMa')}
          </Button>
        </div>
        <pre className="overflow-x-auto rounded bg-subtle p-3 text-xs leading-relaxed text-content-primary">{doanMa}</pre>
        <p className="mt-1 text-xs text-content-muted">{t('troLyAiBotNhung.khoaApiHuongDan')}</p>
      </div>

      {/*
        Xoay là hành động PHÁ HỎNG: khoá nằm lộ trong mã trang của khách, cách duy nhất thu hồi nó là làm nó SAI — mọi trang
        đang nhúng tắt bong bóng ngay, kể cả trang người vận hành đã quên từng dán vào. Nên phải hỏi.
      */}
      <HopXacNhan
        open={hoiXoay}
        onClose={() => setHoiXoay(false)}
        tieuDe={t('troLyAiBotNhung.xoayKhoaHoi')}
        than={t('troLyAiBotNhung.xoayKhoaCanhBao')}
        nhanXacNhan={t('troLyAiBotNhung.xoayKhoaLam')}
        dangChay={xoay.isPending}
        loi={xoay.error}
        onXacNhan={() =>
          botId &&
          xoay.mutate(botId, {
            onSuccess: (b) => {
              onXoay(b.widget.embedKey);
              // HIỆN luôn khoá mới: việc tiếp theo chắc chắn là chép nó đi gửi.
              setHien(true);
              setHoiXoay(false);
              toast.success(t('troLyAiBotNhung.xoayKhoaXong'));
            },
          })
        }
      />
    </div>
  );
}

export function TabNhung({
  f, dat, suaDuoc, nguonDuLieu, botId,
}: TabProps & { nguonDuLieu: LuaChon[]; botId: string | null }) {
  const { t, locale } = useT();
  const moTaLoi = useMoTaLoi();
  const w = f.widget;
  const datW = (p: Partial<typeof w>) => dat('widget', { ...w, ...p });
  const moGoc = moMoiGoc(w.allowedOrigins);
  const kiemVai = w.enabled && !!w.vai;

  /*
   * Danh mục vai cho bot NHÚNG: vai của tổ chức (API vai trò platform), LOẠI vai quản trị — vai sửa được ma trận quyền
   * đứng ngoài ma trận, gán cho khung nhúng là mở mọi bảng cho bất kỳ ai xem trang. Backend cũng từ chối ở chỗ cấp token;
   * loại ở đây để người vận hành không phải chọn rồi mới biết là không được.
   */
  const vaiToChuc = useVaiTroToChuc(w.enabled);
  const taiKhoan = useTaiKhoanQuyen(w.enabled);
  const vaiQuanTri = new Set((taiKhoan.data?.accounts ?? []).filter((a) => a.canManage).map((a) => a.role));
  const vaiChonDuoc = (vaiToChuc.data ?? [])
    .filter((v) => v.ma !== 'TENANT_ADMIN' && !vaiQuanTri.has(v.ma))
    .map((v) => ({ value: v.ma, label: v.ten || v.ma }));
  const tenVai = (ma: string) => vaiToChuc.data?.find((v) => v.ma === ma)?.ten || ma;

  /*
   * Danh mục mô hình — cùng nguồn với ô chọn ở màn trò chuyện. Trả CẢ mục chưa cấu hình kèm lý do: viết cứng thì "không có
   * Anthropic" và "Anthropic thiếu khoá" trông giống hệt nhau. Không gác bằng `enabled`: người vận hành chọn mô hình trước
   * khi bật khung.
   */
  const danhMucModel = useSucKhoeTroLy();
  const modelChonDuoc = (() => {
    const ds = (danhMucModel.data?.models ?? []).map((m) => ({
      value: khoaModel(m),
      label: tenTrongDanhMuc(m),
      // KHÔNG `disabled`: mục mờ không nói được VÌ SAO nó mờ; chọn được rồi đọc cảnh báo bên dưới là một đường đi tới
      // chỗ sửa.
      title: m.available ? m.model : m.note || t('troLyAiBotNhung.moHinhChuaCauHinh'),
    }));
    // Ô render NGAY, `/health` về sau — antd hiện thẳng `value` khi không có mục khớp. Mục tạm giữ đúng tên đã chọn.
    const dang = w.model;
    return dang && !ds.some((o) => o.value === dang) ? [...ds, { value: dang, label: nhanTuKhoa(dang), title: dang }] : ds;
  })();

  const canhBaoModel = ((): CauMau => {
    if (!w.model) return { chu: t('troLyAiBotNhung.moHinhNhungGhi'), mau: 'text-content-muted' };
    if (danhMucModel.isPending) return { chu: '', mau: 'text-content-muted' };
    // Hỏi không được thì KHÔNG kết luận.
    if (danhMucModel.isError) return { chu: t('troLyAiBotNhung.moHinhNhungKhongDoDuoc'), mau: 'text-content-muted' };
    // So bằng KHOÁ, không bằng `provider`: hai mục cùng nhà cung cấp là hai lựa chọn khác nhau.
    const m = danhMucModel.data.models?.find((x) => khoaModel(x) === w.model);
    if (!m) return { chu: t('troLyAiBotNhung.moHinhNhungLa', { ten: w.model }), mau: 'text-warning-700' };
    if (!m.available) {
      return { chu: t('troLyAiBotNhung.moHinhNhungThieuKhoa', { ten: tenTrongDanhMuc(m) }), mau: 'text-danger-700' };
    }
    return { chu: t('troLyAiBotNhung.moHinhNhungDangDung', { ma: m.model }), mau: 'text-content-muted' };
  })();

  /*
   * Quyền của vai trên ĐÚNG nguồn của bot. Ma trận khoá theo CẶP (vai, nguồn): tích đủ ở nguồn A không có tác dụng ở
   * nguồn B. Ba trạng thái khác nhau — có quyền, chỉ có quyền ở nguồn khác, bot chưa gắn nguồn — trước đây hiện y hệt và
   * hai ca sau hỏng IM LẶNG. Nên đi hỏi quyền hiệu lực của đúng vai ấy, lọc theo MỌI nguồn của bot. Nguồn theo khuôn đọc
   * ma trận khuôn (ma trận bảng của nó đang ngủ).
   */
  const quyen = useQuyenHieuLuc(w.vai, kiemVai);
  const nguonHet = useDanhSachNguon(kiemVai);
  const theoKhuon = new Set(
    (nguonHet.data ?? []).filter((s) => s.kind === 'api' || s.cheDo === 'khuon').map((s) => s.id),
  );
  const idKhuon = f.sourceIds.filter((id) => theoKhuon.has(id));
  const maTranKhuon = useMaTranKhuonNhieu(idKhuon, kiemVai);

  const quyenVai = ((): CauMau => {
    if (!w.vai) return { chu: t('troLyAiBotNhung.vaiNhungGhi'), mau: 'text-content-muted' };
    if (!f.sourceIds.length) return { chu: t('troLyAiBotNhung.vaiNhungChuaCoNguon'), mau: 'text-danger-700' };
    const ten = (id: string) => nguonDuLieu.find((n) => n.id === id)?.ten ?? id;
    if (quyen.isPending || nguonHet.isPending || maTranKhuon.some((q) => q.isPending)) {
      return { chu: t('troLyAiBotNhung.vaiNhungDangDo'), mau: 'text-content-muted' };
    }
    // Hỏi không được thì KHÔNG đoán: câu "chưa tick bảng nào" dựng trên lời gọi hỏng làm người vận hành đi tick lại.
    if (quyen.isError || maTranKhuon.some((q) => q.isError)) {
      return { chu: t('troLyAiBotNhung.vaiNhungKhongDoDuoc'), mau: 'text-content-muted' };
    }
    const khuonTheoNguon: Record<string, KhuonGrant[] | undefined> = {};
    idKhuon.forEach((id, i) => {
      khuonTheoNguon[id] = maTranKhuon[i]?.data?.grants;
    });
    const ds = danhGiaVaiNhung(w.vai, f.sourceIds, theoKhuon, quyen.data.sources, khuonTheoNguon);
    const vai = tenVai(w.vai);

    // Một nguồn thì giữ câu cụ thể: nó nói đúng phải làm gì, ở màn nào.
    const [x] = ds;
    if (ds.length === 1 && x) {
      const nguon = ten(x.id);
      switch (x.muc) {
        case 'khuon_none':
          return { chu: t('troLyAiBotNhung.vaiNhungChuaCapKhuon', { vai, nguon }), mau: 'text-danger-700' };
        case 'khuon':
          return { chu: t('troLyAiBotNhung.vaiNhungKhuon', { vai, nguon }), mau: 'text-warning-700' };
        case 'none':
          return { chu: t('troLyAiBotNhung.vaiNhungChuaTick', { vai, nguon }), mau: 'text-danger-700' };
        case 'full':
          // Mức mở nhất thì cảnh báo mạnh nhất — mọi bảng đọc được từ một trang công khai.
          return { chu: t('troLyAiBotNhung.vaiNhungToanBoNguon', { vai, nguon }), mau: 'text-danger-700' };
        default:
          return {
            chu: t('troLyAiBotNhung.vaiNhungCanhBao', { vai, nguon, n: soVN(x.soBang, locale) }),
            mau: 'text-warning-700',
          };
      }
    }

    // Nhiều nguồn: một dòng tóm tắt, rồi chỉ những nguồn phải sửa (tối đa ba) — bức tường chữ thì người ta đọc lướt.
    const coVanDe = ds.filter(laCoVanDe);
    const dong = coVanDe.slice(0, 3).map((d) =>
      d.muc === 'khuon_none'
        ? t('troLyAiBotNhung.vaiNhungDongChuaCapKhuon', { nguon: ten(d.id) })
        : d.muc === 'none'
          ? t('troLyAiBotNhung.vaiNhungDongChuaTick', { nguon: ten(d.id) })
          : t('troLyAiBotNhung.vaiNhungDongToanBo', { nguon: ten(d.id) }),
    );
    if (coVanDe.length > 3) dong.push(t('troLyAiBotNhung.vaiNhungConLai', { n: soVN(coVanDe.length - 3, locale) }));
    return {
      chu: [
        t('troLyAiBotNhung.vaiNhungTomTat', {
          vai,
          ok: soVN(soNguonDocDuoc(ds), locale),
          tong: soVN(ds.length, locale),
        }),
        ...dong,
      ].join(' '),
      // Mức nặng nhất quyết định màu: một nguồn mở TOÀN BỘ giữa bốn nguồn đã khoá vẫn là một trang đọc được mọi bảng.
      mau: coVanDe.length ? 'text-danger-700' : 'text-warning-700',
    };
  })();

  const mauSai = !!w.primaryColor && !MAU_HOP_LE.test(w.primaryColor.trim());

  return (
    <Khoi>
      {/* Nói TRƯỚC, trước cả công tắc: người đọc từ trên xuống sẽ bật khung, chép khoá, gửi đi rồi mới phát hiện. */}
      <div className="rounded border border-warning-500 bg-warning-50 p-3">
        <p className="flex items-center gap-1.5 text-xs font-medium text-warning-700">
          <WarningOutlined className="text-xs" />
          {t('troLyAiBotNhung.nhungChuaChay')}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-content-secondary">{t('troLyAiBotNhung.nhungChuaChayGhi')}</p>
      </div>

      {/* Công tắc RIÊNG, không dùng chung với `active`: bot chạy trong hệ thống ≠ bot trả lời cả internet. */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-default p-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-content-primary">{t('troLyAiBotNhung.moKhungRaNgoai')}</p>
          <p className="text-xs leading-relaxed text-content-secondary">{t('troLyAiBotNhung.moKhungGhi')}</p>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <Switch size="small" checked={w.enabled} disabled={!suaDuoc} onChange={(v) => datW({ enabled: v })} />
          {w.enabled ? t('troLyAiBotNhung.dangMo') : t('troLyAiBotNhung.dangDong')}
        </label>
      </div>

      {/* Người đọc thấy chữ "khoá" và tưởng là mật khẩu — rồi nới trần tần suất cho đỡ phiền, tức tháo đúng hàng rào còn lại. */}
      <div className="rounded border border-default bg-subtle p-3 text-xs leading-relaxed text-content-secondary">
        <p className="mb-1 font-medium text-content-primary">{t('troLyAiBotNhung.khoaKhongPhaiMatKhau')}</p>
        {t('troLyAiBotNhung.khoaGhi')}
        <ul className="mt-1 list-disc space-y-0.5 ps-4">
          <li>
            <b>{t('troLyAiBotNhung.khoaDsGocDam')}</b> {t('troLyAiBotNhung.khoaDsGocSau')}
          </li>
          <li>
            <b>{t('troLyAiBotNhung.khoaTranDam')}</b> {t('troLyAiBotNhung.khoaTranSau')}
          </li>
        </ul>
      </div>

      {w.embedKey ? (
        <KhoiNhung
          khoa={w.embedKey}
          botId={botId}
          suaDuoc={suaDuoc}
          onXoay={(moi) => datW({ embedKey: moi })}
        />
      ) : (
        <p className="rounded bg-subtle px-3 py-2 text-xs text-content-secondary">{t('troLyAiBotNhung.khoaSinhKhiLuu')}</p>
      )}

      {/*
        Ba trạng thái, ba câu: rỗng = chưa ai khai (đóng) · `*` = có người quyết định mở · danh sách = đóng trừ những gốc
        này. Gộp rỗng với `*` là xoá mất khác biệt về Ý ĐỊNH.
      */}
      <div>
        <p className="mb-1 text-xs font-medium text-content-secondary">
          {t('troLyAiBotNhung.gocDuocPhep')}
          {moGoc ? (
            <span className="ms-2 font-normal text-danger-700">{t('troLyAiBotNhung.gocLaSao')}</span>
          ) : !w.allowedOrigins.length ? (
            <span className="ms-2 font-normal text-warning-700">{t('troLyAiBotNhung.gocRong')}</span>
          ) : null}
        </p>
        <ODanhSachDong
          autoSize={{ minRows: 2, maxRows: 8 }}
          disabled={!suaDuoc}
          aria-label={t('troLyAiBotNhung.gocDuocPhep')}
          placeholder={t('troLyAiBotNhung.gocViDu')}
          giaTri={w.allowedOrigins}
          onDoi={(ds) => datW({ allowedOrigins: ds })}
        />
        <p className="mt-1 text-xs text-content-muted">{t('troLyAiBotNhung.gocGhiChu')}</p>
        {/* Cảnh báo hiện SAU khi gõ `*`, không thường trực — cảnh báo lúc nào cũng hiện là cảnh báo không ai đọc. */}
        {moGoc ? (
          <div className="mt-2 flex items-start gap-1.5 rounded border border-danger-500 bg-danger-50 px-3 py-2 text-xs leading-relaxed text-danger-700">
            <WarningOutlined className="mt-px shrink-0 text-xs" />
            <span>
              <b>{t('troLyAiBotNhung.gocCanhBaoDam')}</b> {t('troLyAiBotNhung.gocCanhBaoSau')}
            </span>
          </div>
        ) : null}
      </div>

      {/*
        Vai của bot nhúng — công tắc nguy hiểm nhất ở màn này. Ma trận quyền dữ liệu mặc định ĐÓNG: để trống thì bot nhúng
        không đọc bảng nào. Chọn một vai là mở cho KHÁCH VÃNG LAI đúng những bảng đã cấp cho vai ấy — nên không có giá trị
        gợi ý, và câu chú thích nói thẳng hậu quả.
      */}
      <Field label={t('troLyAiBotNhung.vaiNhung')}>
        {(p) => (
          <div>
            <Select<string>
              {...p}
              allowClear
              className="w-full"
              disabled={!suaDuoc}
              loading={vaiToChuc.isFetching}
              options={vaiChonDuoc}
              placeholder={t('troLyAiBotNhung.vaiNhungRong')}
              value={w.vai || undefined}
              onChange={(v) => datW({ vai: v ?? '' })}
            />
            <span className={cn('mt-0.5 block text-xs', quyenVai.mau)}>{quyenVai.chu}</span>
            {vaiToChuc.error ? (
              <span className="mt-0.5 block text-xs text-danger-700">{moTaLoi(vaiToChuc.error)}</span>
            ) : null}
          </div>
        )}
      </Field>

      {/* Mô hình đứng ngay dưới vai, trên trần tần suất: ba ô đọc thành một câu — đọc được gì, trả lời bằng gì, tiêu bao nhiêu. */}
      <Field label={t('troLyAiBotNhung.moHinhNhung')}>
        {(p) => (
          <div>
            <Select<string>
              {...p}
              allowClear
              className="w-full"
              disabled={!suaDuoc}
              loading={danhMucModel.isPending}
              options={modelChonDuoc}
              placeholder={tenMucMacDinh(danhMucModel.data) || t('troLyAiBotNhung.moHinhNhungMacDinh')}
              value={w.model || undefined}
              onChange={(v) => datW({ model: v ?? '' })}
            />
            {canhBaoModel.chu ? <span className={cn('mt-0.5 block text-xs', canhBaoModel.mau)}>{canhBaoModel.chu}</span> : null}
          </div>
        )}
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('troLyAiBotNhung.tranChung')}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={1}
              disabled={!suaDuoc}
              value={String(w.rateLimitPerMinute)}
              onChange={(e) => datW({ rateLimitPerMinute: Math.max(1, Number(e.target.value) || 1) })}
            />
          )}
        </Field>
        <Field label={t('troLyAiBotNhung.tranMotNguoi')} hint={t('troLyAiBotNhung.tranMotNguoiGhi')}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={1}
              disabled={!suaDuoc}
              value={String(w.rateLimitPerIpPerMinute)}
              onChange={(e) => datW({ rateLimitPerIpPerMinute: Math.max(1, Number(e.target.value) || 1) })}
            />
          )}
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('troLyAiBotNhung.tieuDeKhung')}>
          {(p) => (
            <Input
              {...p}
              disabled={!suaDuoc}
              value={w.title}
              onChange={(e) => datW({ title: e.target.value })}
              placeholder={t('troLyAiBotViDu.khungTieuDe')}
            />
          )}
        </Field>
        {/* Hai ô, một giá trị: nhãn hiện tách riêng, mỗi ô mang `aria-label` của nó. */}
        <div className="text-xs font-medium text-content-secondary">
          <p>{t('troLyAiBotNhung.mauChuDao')}</p>
          <div className="mt-1 flex items-center gap-2">
            {/* Ô chọn màu NGUYÊN BẢN của trình duyệt: mang theo bộ chọn của hệ điều hành và ống hút màu. */}
            <input
              type="color"
              disabled={!suaDuoc}
              aria-label={t('troLyAiBotNhung.chonMau')}
              value={mauSauChuSo(w.primaryColor, WIDGET_MAU_MAC_DINH)}
              onChange={(e) => datW({ primaryColor: e.target.value })}
              className="size-8 shrink-0 cursor-pointer rounded border border-default bg-surface p-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            />
            {/* Ô chữ ở lại: mã màu là thứ DÁN ĐƯỢC từ bản hướng dẫn thương hiệu. */}
            <Input
              disabled={!suaDuoc}
              aria-label={t('troLyAiBotNhung.mauChuDao')}
              aria-invalid={mauSai}
              status={mauSai ? 'error' : undefined}
              value={w.primaryColor}
              onChange={(e) => datW({ primaryColor: e.target.value })}
              placeholder={WIDGET_MAU_MAC_DINH}
            />
          </div>
          {/* Máy chủ từ chối chuỗi sai dạng — nói ra ngay lúc gõ, không để lỗi hiện lúc bấm Lưu. */}
          {mauSai ? <span className="mt-0.5 block font-normal text-danger-700">{t('troLyAiBotNhung.mauSaiDang')}</span> : null}
        </div>
      </div>

      <div role="group" aria-label={t('troLyAiBotNhung.viTri')} className="flex flex-wrap gap-2">
        {(['bottom-right', 'bottom-left'] as WidgetPosition[]).map((v) => (
          <button
            key={v}
            type="button"
            disabled={!suaDuoc}
            aria-pressed={w.position === v}
            onClick={() => datW({ position: v })}
            className={cn(
              'rounded border px-3 py-1.5 text-xs transition disabled:opacity-60',
              w.position === v
                ? 'border-primary-600 bg-primary-50 font-medium text-primary-700'
                : 'border-default text-content-secondary hover:bg-subtle',
            )}
          >
            {v === 'bottom-right' ? t('troLyAiBotNhung.gocDuoiPhai') : t('troLyAiBotNhung.gocDuoiTrai')}
          </button>
        ))}
      </div>
    </Khoi>
  );
}
